import { apiFetch } from "./client";

/**
 * IMPORTING AN IMAGE — two steps, and neither of them sends the file to our API.
 *
 * `POST /files` hands back a pre-signed S3 address; the browser then PUTs the
 * bytes straight to the bucket. The file never crosses the API, which is what
 * keeps a ten-megabyte logo from being carried through a Lambda's request body.
 *
 * ⚠️ `isPublic: true`, and that is not a detail. The other bucket is private and
 * hands out signed, EXPIRING addresses — a header logo stored there would render
 * for the organiser who uploaded it and turn into a broken image for participants
 * a few hours later. The front office is a public site: what it displays has to
 * be publicly readable.
 */

type Signed = { signedUrl: string; uuid: string; url?: string };

/**
 * A ceiling, because nobody checks. The header of an event site is the first
 * thing a participant loads, often on a phone, often on the venue's wifi — an
 * untouched camera export there costs everyone several seconds.
 */
const MAX_BYTES = 5 * 1024 * 1024;

export class UploadError extends Error {}

export async function uploadImage(
  file: File,
  signal?: AbortSignal,
): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new UploadError("Ce fichier n'est pas une image.");
  }
  if (file.size > MAX_BYTES) {
    const mo = Math.round((file.size / 1024 / 1024) * 10) / 10;
    throw new UploadError(`Image trop lourde (${mo} Mo, maximum 5 Mo).`);
  }

  const signed = await apiFetch<Signed>("/files", {
    api: "apix",
    method: "POST",
    body: { contentType: file.type, isPublic: true },
    signal,
  });

  // `url` is only returned for the public bucket. Without it there is nothing to
  // store, and storing the signed address instead would look like it worked
  // until the signature expired.
  if (!signed?.signedUrl || !signed?.url) {
    throw new UploadError("Le service de fichiers n'a pas répondu comme prévu.");
  }

  const put = await fetch(signed.signedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
    signal,
  });

  if (!put.ok) {
    throw new UploadError(`Le transfert a échoué (${put.status}).`);
  }

  return signed.url;
}

/**
 * ═══ UNE PIÈCE JOINTE DE PARTICIPANT — même mécanique, seau PRIVÉ ═══
 *
 * Un CV, un justificatif : ce que quelqu'un dépose dans un formulaire n'a rien à
 * faire sur une adresse publique et permanente. Le fichier part donc dans le seau
 * privé, et on ne garde que sa CLÉ (voir `funnel/attachment.ts`). Pour l'ouvrir,
 * `attachmentUrl` demande une adresse signée de quelques minutes.
 *
 * Le type accepté et le plafond viennent du back office, champ par champ ; ils sont
 * vérifiés AVANT de demander une adresse, pour ne pas laisser une clé orpheline.
 */
export async function uploadAttachment(
  file: File,
  maxBytes: number,
  signal?: AbortSignal,
): Promise<string> {
  if (file.size > maxBytes) {
    throw new UploadError("too-big");
  }
  const signed = await apiFetch<Signed>("/files", {
    api: "apix",
    method: "POST",
    /* `kind` : la pièce jointe va sous SON préfixe du seau privé, ce qui borne le
       balayage des orphelins à ce qui vient d'ici (api/src/helpers/attachments.js). */
    body: {
      contentType: file.type || "application/octet-stream",
      isPublic: false,
      kind: "attachment",
    },
    signal,
  });
  if (!signed?.signedUrl || !signed?.uuid) {
    throw new UploadError("Le service de fichiers n'a pas répondu comme prévu.");
  }
  const put = await fetch(signed.signedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type || "application/octet-stream" },
    signal,
  });
  if (!put.ok) {
    throw new UploadError(`Le transfert a échoué (${put.status}).`);
  }
  return signed.uuid;
}

export async function attachmentUrl(uuid: string, name: string): Promise<string> {
  const q = `?kind=attachment${name ? `&name=${encodeURIComponent(name)}` : ""}`;
  const { url } = await apiFetch<{ url: string }>(
    `/files/${encodeURIComponent(uuid)}/url${q}`,
    { api: "apix" },
  );
  return url;
}

/**
 * ═══ LE PORTRAIT QU'UN PARTICIPANT CHOISIT — seau privé, SANS préfixe ═══
 *
 * C'est la photo de soi que la reconnaissance compare aux photos de l'événement (voir
 * `album/face.ts`). Elle part dans le seau privé, comme celle que le back office dépose
 * depuis l'onglet « Visages » d'une fiche, et on n'en garde que la CLÉ : c'est elle que
 * l'API donne à Rekognition.
 *
 * ⚠️ PAS DE `kind`, ET C'EST VOLONTAIRE. Une pièce jointe va sous son préfixe pour que
 * le balayage des orphelins sache où chercher ; un portrait doit atterrir là où
 * `indexFaces` va le lire, c'est-à-dire à l'endroit qu'utilise déjà le back office.
 */
/** Le plus grand côté du portrait envoyé, en pixels. */
export const PORTRAIT_MAX_SIDE = 1600;
/** La qualité JPEG du portrait envoyé. */
export const PORTRAIT_QUALITY = 0.85;

/**
 * RÉDUIT UNE PHOTO AVANT DE L'ENVOYER, dans le navigateur.
 *
 * Une photo de téléphone pèse plusieurs mégaoctets et fait quatre mille pixels de côté.
 * Rien n'en a besoin : la reconnaissance demande quelques dizaines de pixels par visage,
 * l'avatar en affiche cinquante-six, et ce fichier restera dans le seau tant que le
 * compte existera. Redimensionnée à 1 600 px de plus grand côté en JPEG, la même photo
 * pèse quelques centaines de kilo-octets, et l'envoi sur le wifi d'une salle cesse
 * d'être le moment le plus long du geste.
 *
 * ⚠️ L'ORIENTATION EST CELLE DE LA PRISE DE VUE. Un téléphone tenu droit écrit une image
 * couchée et note dans ses métadonnées qu'il faut la tourner ; un canevas les ignore.
 * `createImageBitmap` avec `imageOrientation: "from-image"` applique la rotation avant
 * de dessiner : sans ça, un portrait sur deux partait couché, et Rekognition y cherchait
 * un visage à l'horizontale.
 *
 * ⚠️ ET QUAND ON NE SAIT PAS, ON ENVOIE TEL QUEL. Un format que le navigateur ne décode
 * pas, ou un canevas refusé, ne doivent pas empêcher de poser sa photo : l'original
 * part, plus lourd, et le serveur fait le reste.
 */
export async function shrinkImage(
  file: File,
  maxSide = PORTRAIT_MAX_SIDE,
  quality = PORTRAIT_QUALITY,
): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const paint = canvas.getContext("2d");
    if (!paint) return file;
    paint.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((done) =>
      canvas.toBlob(done, "image/jpeg", quality),
    );
    /* Un JPEG plus lourd que l'original (un petit PNG très simple, par exemple) n'a
       rien gagné : on garde l'original. */
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

export async function uploadPortrait(
  file: File,
  signal?: AbortSignal,
): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new UploadError("Ce fichier n'est pas une image.");
  }
  /* Plus large que pour un logo : une photo prise à l'instant avec un téléphone pèse
     volontiers plusieurs mégaoctets, et la refuser ferait échouer le cas le plus
     courant. C'est un plafond sur ce qu'on accepte de LIRE ; ce qui part est réduit. */
  if (file.size > 12 * 1024 * 1024) {
    const mo = Math.round((file.size / 1024 / 1024) * 10) / 10;
    throw new UploadError(`Photo trop lourde (${mo} Mo, maximum 12 Mo).`);
  }

  const body = await shrinkImage(file);
  const contentType = body.type || file.type;

  const signed = await apiFetch<Signed>("/files", {
    api: "apix",
    method: "POST",
    body: { contentType, isPublic: false },
    signal,
  });
  if (!signed?.signedUrl || !signed?.uuid) {
    throw new UploadError("Le service de fichiers n'a pas répondu comme prévu.");
  }

  const put = await fetch(signed.signedUrl, {
    method: "PUT",
    body,
    headers: { "Content-Type": contentType },
    signal,
  });
  if (!put.ok) {
    throw new UploadError(`Le transfert a échoué (${put.status}).`);
  }
  return signed.uuid;
}

/**
 * ═══ LES IMAGES DES BLOCS HTML : UNE ADRESSE, JAMAIS LE FICHIER DANS LE TEXTE ═══
 *
 * ⚠️ SANS ADRESSE D'ENVOI, SUNEDITOR ÉCRIT L'IMAGE EN BASE64 DANS LE HTML — bouton image,
 * image collée ou glissée. Le 2026-09-25, quatre blocs de l'événement 744 portaient ainsi
 * 1,1 Mo d'images dans ses réglages : chaque visiteur les téléchargeait avant d'afficher la
 * page, et la ligne, trop grosse pour être triée par MySQL, a fait tomber tous les emails
 * de l'événement. Une image de bloc passe donc par ici : réduite, recompressée, déposée
 * dans le seau public, et le HTML n'en garde que l'adresse.
 *
 * 2 000 px : un bloc du site fait au plus 1 200 px de large (`--content`), et un écran
 * haute densité en montre le double sans flou visible au-delà.
 */
export const EDITOR_IMAGE_MAX_WIDTH = 2000;
const EDITOR_JPEG_QUALITY = 0.82;

/** La taille dessinée : jamais agrandie, ramenée à `max` de large, proportions gardées. */
export function fitWidth(
  width: number,
  height: number,
  max: number,
): { width: number; height: number } {
  const scale = Math.min(1, max / Math.max(1, width));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Le nom du fichier une fois recompressé : la même racine, l'extension du nouveau format. */
export function renamedFor(name: string, type: string): string {
  const ext = type === "image/png" ? "png" : "jpg";
  const base = name.replace(/\.[a-z0-9]+$/i, "") || "image";
  return `${base}.${ext}`;
}

/* Un pas de seize pixels suffit : une transparence utile n'est jamais un pixel isolé. */
function hasTransparency(paint: CanvasRenderingContext2D, width: number, height: number) {
  const { data } = paint.getImageData(0, 0, width, height);
  for (let i = 3; i < data.length; i += 4 * 16) {
    if (data[i]! < 255) return true;
  }
  return false;
}

/**
 * RECOMPRESSE UNE IMAGE POUR LE WEB, transparence comprise — là où `shrinkImage`, faite
 * pour des photos, passe tout en JPEG.
 *
 * PNG, JPEG, WebP : ramenés à `maxWidth` ; une transparence reste en PNG, le reste part en
 * JPEG sur fond blanc (sans ce fond, le transparent devient noir). GIF (animations) et SVG
 * partent tels quels : les redessiner les casserait. Un résultat plus lourd que l'original
 * — un petit logo déjà optimisé — rend l'original. Et comme `shrinkImage`, quand le
 * navigateur ne sait pas faire, l'original part : mieux vaut lourd que rien.
 */
export async function compressForWeb(
  file: File,
  maxWidth = EDITOR_IMAGE_MAX_WIDTH,
): Promise<File> {
  if (!/^image\/(png|jpeg|webp)$/.test(file.type)) return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const { width, height } = fitWidth(bitmap.width, bitmap.height, maxWidth);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const paint = canvas.getContext("2d");
    if (!paint) return file;
    paint.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const alpha = hasTransparency(paint, width, height);
    let out = canvas;
    if (!alpha) {
      const flat = document.createElement("canvas");
      flat.width = width;
      flat.height = height;
      const flatPaint = flat.getContext("2d");
      if (flatPaint) {
        flatPaint.fillStyle = "#ffffff";
        flatPaint.fillRect(0, 0, width, height);
        flatPaint.drawImage(canvas, 0, 0);
        out = flat;
      }
    }
    const type = alpha ? "image/png" : "image/jpeg";
    const blob = await new Promise<Blob | null>((done) =>
      out.toBlob(done, type, alpha ? undefined : EDITOR_JPEG_QUALITY),
    );
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], renamedFor(file.name, type), { type });
  } catch {
    return file;
  }
}

/** Une image posée dans un bloc HTML : recompressée, déposée dans le seau public. Rend son adresse. */
export async function uploadEditorImage(file: File, signal?: AbortSignal): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new UploadError("Ce fichier n'est pas une image.");
  }
  return uploadImage(await compressForWeb(file), signal);
}

/**
 * UNE IMAGE ÉCRITE DANS DU HTML (`data:image/…;base64,…`), redevenue un fichier — pour
 * repartir par `uploadEditorImage` quand on colle le contenu d'un document. `null` si ce
 * n'est pas une image en base64 lisible.
 */
export function dataUrlToFile(dataUrl: string, name: string): File | null {
  const match = /^data:(image\/[a-z0-9.+-]+);base64,([a-z0-9+/=\s]+)$/i.exec(dataUrl.trim());
  if (!match) return null;
  try {
    const type = match[1]!.toLowerCase();
    const binary = atob(match[2]!.replace(/\s+/g, ""));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const ext = type.split("/")[1]!.replace("jpeg", "jpg").replace("svg+xml", "svg");
    return new File([bytes], `${name}.${ext}`, { type });
  } catch {
    return null;
  }
}
